import { useState } from 'react';
import { useRouter } from 'next/router';
import useLocales from '@hooks/useLocales';
import { DropdownViewOption, UserDataJSONAPI } from '~/sections/user-profile/types';
import { useMutation } from 'react-query';
import { CurrentUser } from '~/api/scheme';
import useCurrentUser from '~/hooks/useCurrentUser';

type AccountPopoverData = {
  user: UserDataJSONAPI | undefined;
  open: boolean;
  anchorEl: HTMLElement | null;
  handleOpen: (event: React.MouseEvent<HTMLElement>) => void;
  handleClose: () => void;
  viewOptions: DropdownViewOption[];
};

const useAccountPopover = (): AccountPopoverData => {
  const { t } = useLocales();
  const router = useRouter();
  const [open, setOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const mutation = useMutation(() => CurrentUser.signout(), {
    onSuccess() {
      router.push({ pathname: '/login' });
    },
  });

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  const handleLogout = () => {
    mutation.mutate();
  };

  const { currentUser } = useCurrentUser();

  const viewOptions = [
    {
      value: 'myProfile',
      label: `${t('dashboard.user.my_profile')}`,
      icon: '/icons/iconly/bulk/profile.svg',
      onPress: () =>
        router.push({
          pathname: '/user-profile',
          query: 'view=myProfile',
        }),
    },
    {
      value: 'myCompany',
      label: `${t('dashboard.user.my_company')}`,
      icon: '/icons/iconly/bulk/work.svg',
      onPress: () =>
        router.push({
          pathname: '/user-profile',
          query: 'view=myCompany',
        }),
    },
    {
      value: 'logout',
      label: `${t('dashboard.user.logout')}`,
      icon: '',
      onPress: () => {
        handleLogout();
      },
    },
  ];

  return {
    user: currentUser,
    open,
    anchorEl,
    handleOpen,
    handleClose,
    viewOptions,
  };
};

export default useAccountPopover;
